/**
 * src/lib/voice/enqueue.js — Voice Sessions V-4 intake helper.
 *
 * Plan: docs/plans/2026-05-01-voice-sessions-and-image-sync-plan.md (V-4).
 *
 * The single entry point any UI calls when the user produces an audio
 * blob (file picker, MediaRecorder stop, etc.). Returns synchronously
 * after writing one IndexedDB row — never awaits the network.
 *
 * The worker singleton (V-2) drains queued recordings and dispatches
 * them via voiceProcessFn (V-4 default).
 */
import { createRecording } from '../voiceQueue.js';
import { logVoiceQueueEvent } from './logVoiceQueueEvent.js';
import { isValidScope } from './voiceScopes.js';

function newRecordingId() {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `rec-${Date.now()}-${Math.random().toString(36).slice(2, 11)}`;
}

export async function enqueueVoiceSession({
  blob,
  scope_type,
  scope_id,
  scope_label,
  file_name,
  language,
  recording_id: callerRecordingId,
  duration_sec,
  context_hint,
} = {}) {
  if (!blob) {
    throw new Error('[enqueueVoiceSession] blob is required');
  }
  if (!scope_type) {
    throw new Error('[enqueueVoiceSession] scope_type is required');
  }
  if (!isValidScope(scope_type)) {
    throw new Error(`[enqueueVoiceSession] unsupported scope_type: ${scope_type}`);
  }

  // Bug 1 (CEO 2026-05-06): every new voice note created a phantom
  // blank "Processing..." breadcrumb that never cleared. Root cause was
  // useEntityMic.js generating its own recordingId for voice:record_stop
  // (drives the optimistic row in useVoiceBreadcrumbs) while
  // enqueueVoiceSession independently generated its own UUID for IDB
  // queue + voice_records.idempotency_key. The two never matched, so
  // the optimistic row never cleared. Accepting a caller-supplied
  // recording_id lets the caller emit voice:record_stop and enqueue
  // with the same id — the optimistic row clears the moment the server
  // row lands.
  const recording_id = callerRecordingId || newRecordingId();
  // CEO ask 2026-05-03: tray banner showed "Project · f751e3" (UUID slice)
  // instead of project name. Persist a human label at enqueue time so the
  // tray reads it from IDB without a context lookup (works after refresh
  // even before AppDataContext rehydrates).
  await createRecording({
    recording_id,
    optimistic_uuid: recording_id,
    status: 'queued',
    scope_type,
    scope_id: scope_id ?? null,
    scope_label: scope_label || null,
    file_name: file_name || 'voice-note',
    audio_blob: blob,
    mime_type: blob.type || null,
    file_size_bytes: blob.size ?? null,
    language: language ?? null,
    // TICKET-064: persist MediaRecorder duration so processProjectScope
    // can forward it on uploadVoiceFile → voice_uploads.duration_sec.
    // null when caller doesn't supply (file uploads from picker have no
    // sync-derivable duration; that's a separate follow-up).
    duration_sec: typeof duration_sec === 'number' ? duration_sec : null,
    // TICKET-CODEX-3172673721: persist caller-provided contextHint so
    // voiceProcessFn can read rec.context_hint and forward it to the
    // backend classifier / captureVoice. Without this, queued recordings
    // always sent context_hint=undefined regardless of the mic scope.
    context_hint: context_hint ?? null,
    chunks_uploaded: 0,
    total_chunks: 1,
    retry_count: 0,
    created_at: Date.now(),
  });

  logVoiceQueueEvent({
    event: 'enqueue',
    recording_id,
    scope_type,
    scope_id,
  });

  // Best-effort wake-up signal for the worker leader (other tabs ignore).
  try {
    if (typeof BroadcastChannel !== 'undefined') {
      const ch = new BroadcastChannel('acn-voice-queue');
      ch.postMessage({ type: 'enqueued', recording_id });
      ch.close();
    }
  } catch {
    // BroadcastChannel is optional; the worker polls every pollMs anyway.
  }

  return { recording_id };
}
